import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Facebook,
  Instagram,
  Linkedin,
  Link as LinkIcon,
  Play,
  Save,
  Share2,
  Twitter,
  MapPin
} from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import PageHeader from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import useToast from "../hooks/useToast";
import { getSocialLinks, updateSocialLinks } from "../services/socialLinksService";

const socialLinksSchema = z.object({
  instagram: z.string().trim().url("Please enter a valid URL").or(z.literal("")),
  facebook: z.string().trim().url("Please enter a valid URL").or(z.literal("")),
  linkedin: z.string().trim().url("Please enter a valid URL").or(z.literal("")),
  twitter: z.string().trim().url("Please enter a valid URL").or(z.literal("")),
  address: z.string().trim().min(5, "Address must be at least 5 characters").or(z.literal(""))
});

const defaultValues = {
  instagram: "",
  facebook: "",
  linkedin: "",
  twitter: "",
  address: ""
};

export default function SocialLinks() {
  const [isLoading, setIsLoading] = useState(true);
  const { showErrorToast, showSuccessToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting, isDirty }
  } = useForm({
    defaultValues,
    resolver: zodResolver(socialLinksSchema)
  });

  const watchedValues = watch();

  async function loadSocialLinks() {
    setIsLoading(true);
    const response = await getSocialLinks();

    if (response?.success) {
      reset(response.data);
    } else {
      showErrorToast(response?.message || "Unable to load profile settings");
    }
    setIsLoading(false);
  }

  useEffect(() => {
    loadSocialLinks();
  }, []);

  const onSubmit = async (values) => {
    const response = await updateSocialLinks(values);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to update profile settings");
      return;
    }

    showSuccessToast(response.message || "Profile settings updated successfully");
    reset(values); // Reset to new values to clear isDirty state
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Settings"
        title="Organization Profile"
        description="Manage your organization's social media presence and physical address."
      />

      <Card className="border-white/90 bg-white/84">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Configure Profile</CardTitle>
            <p className="mt-2 text-sm text-panel-muted">
              Update your organization's address and social media profiles. These will be displayed on the public website.
            </p>
          </div>
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Share2 className="h-5 w-5" />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="rounded-3xl border border-slate-100 bg-slate-50/80 p-6 text-sm text-panel-muted">
              Loading social links configuration...
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                {/* Instagram */}
                <div className="space-y-2">
                  <FieldLabel className="flex items-center gap-2">
                    <Instagram className="h-4 w-4 text-pink-600" />
                    Instagram URL
                  </FieldLabel>
                  <Input
                    placeholder="https://instagram.com/yourprofile"
                    {...register("instagram")}
                  />
                  {errors.instagram ? (
                    <FieldError>{errors.instagram.message}</FieldError>
                  ) : null}
                </div>

                {/* Facebook */}
                <div className="space-y-2">
                  <FieldLabel className="flex items-center gap-2">
                    <Facebook className="h-4 w-4 text-blue-600" />
                    Facebook URL
                  </FieldLabel>
                  <Input
                    placeholder="https://facebook.com/yourpage"
                    {...register("facebook")}
                  />
                  {errors.facebook ? (
                    <FieldError>{errors.facebook.message}</FieldError>
                  ) : null}
                </div>

                {/* LinkedIn */}
                <div className="space-y-2">
                  <FieldLabel className="flex items-center gap-2">
                    <Linkedin className="h-4 w-4 text-blue-700" />
                    LinkedIn URL
                  </FieldLabel>
                  <Input
                    placeholder="https://linkedin.com/company/yourcompany"
                    {...register("linkedin")}
                  />
                  {errors.linkedin ? (
                    <FieldError>{errors.linkedin.message}</FieldError>
                  ) : null}
                </div>

                {/* Twitter */}
                <div className="space-y-2">
                  <FieldLabel className="flex items-center gap-2">
                    <Twitter className="h-4 w-4 text-sky-500" />
                    Twitter URL
                  </FieldLabel>
                  <Input
                    placeholder="https://twitter.com/yourprofile"
                    {...register("twitter")}
                  />
                  {errors.twitter ? (
                    <FieldError>{errors.twitter.message}</FieldError>
                  ) : null}
                </div>

                {/* Address */}
                <div className="space-y-2 md:col-span-2">
                  <FieldLabel className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-slate-600" />
                    Office Address
                  </FieldLabel>
                  <textarea
                    className="flex min-h-[100px] w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Enter your organization's full physical address"
                    {...register("address")}
                  />
                  {errors.address ? (
                    <FieldError>{errors.address.message}</FieldError>
                  ) : null}
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  disabled={isSubmitting || !isDirty}
                  className="w-full md:w-auto"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
      
      {!isLoading && (
        <Card className="border-white/90 bg-white/84">
          <CardHeader>
            <CardTitle className="text-sm font-semibold uppercase tracking-wider text-panel-muted">
              Live Preview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {[
                { id: "instagram", icon: Instagram, color: "text-pink-600", bg: "bg-pink-50" },
                { id: "facebook", icon: Facebook, color: "text-blue-600", bg: "bg-blue-50" },
                { id: "linkedin", icon: Linkedin, color: "text-blue-700", bg: "bg-blue-50" },
                { id: "twitter", icon: Twitter, color: "text-sky-500", bg: "bg-sky-50" }
              ].map((platform) => {
                const url = watchedValues[platform.id];
                if (!url) return null;

                return (
                  <a
                    key={platform.id}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-3 rounded-2xl border border-slate-100 ${platform.bg} px-4 py-3 transition-all hover:scale-105 hover:shadow-md`}
                  >
                    <platform.icon className={`h-5 w-5 ${platform.color}`} />
                    <span className="text-sm font-medium text-panel-ink capitalize">{platform.id}</span>
                  </a>
                );
              })}
              
              {watchedValues.address && (
                <div className="flex w-full items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                  <MapPin className="mt-1 h-5 w-5 text-slate-600" />
                  <div>
                    <span className="block text-xs font-semibold uppercase tracking-wider text-panel-muted">Office Address</span>
                    <p className="mt-1 text-sm text-panel-ink whitespace-pre-line">{watchedValues.address}</p>
                  </div>
                </div>
              )}

              {!['instagram', 'facebook', 'linkedin', 'twitter', 'address'].some(p => watchedValues[p]) && (
                <p className="text-sm italic text-panel-muted">
                  No profile information to preview. Add details above to see how they'll appear.
                </p>
              )}
            </div>
            <p className="mt-4 text-xs text-panel-muted italic">
              * Links updated here will reflect across all public brand touchpoints immediately after saving.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
